import React, { useEffect, useState } from "react";
import { Field, FieldInputProps, FormikProps, useFormikContext } from "formik";
import { Input, Select, Form as SForm, FormInput as SFormInput, FormSelect as SFormSelect } from "@/components/bp/bp-ui";
import useWeightConversion, { WeightUnit } from "../../Hooks/UseWeightConversion";

type Option = { key: string; value: string; text: string };

interface Props {
    // Formik field name for canonical kg value, e.g. fn("weightInKg")
    nameKg: string;
    // optional Formik field name for unit storage, e.g. fn("weightUnit")
    nameUnit?: string;
    label?: string;
    initialUnit?: WeightUnit;
    selectOptions?: Option[];
    displayPrecisionKg?: number;
    displayPrecisionLbs?: number;
    storagePrecisionKg?: number;
    disabled?: boolean;
}

export default function WeightWithUnitField(props: Props) {
    const {
        nameKg,
        nameUnit,
        label = "Weight",
        initialUnit = "kg",
        selectOptions = [
            { key: "kg", value: "kg", text: "Kilograms (kg)" },
            { key: "lbs", value: "lbs", text: "Pounds (lbs)" },
        ],
        displayPrecisionKg,
        displayPrecisionLbs,
        storagePrecisionKg,
        disabled = false,
    } = props;

    const formik = useFormikContext<any>() as FormikProps<any>;
    const formUnitValue = nameUnit ? (formik.values?.[nameUnit] as WeightUnit | undefined) : undefined;

    // Initialize hook with either form-stored unit or provided initialUnit
    const { unit, setUnit, toDisplay, parseToKg } = useWeightConversion(formUnitValue ?? initialUnit, {
        displayPrecisionKg,
        displayPrecisionLbs,
        storagePrecisionKg
    });

    // Keep local display text so typing isn't janky
    const [displayValue, setDisplayValue] = useState<string>(() => toDisplay(formik.values?.[nameKg]));

    // Sync display when underlying kg or unit changes
    useEffect(() => {
        setDisplayValue(toDisplay(formik.values?.[nameKg]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values?.[nameKg], unit]);

    // If the form's unit field changes externally, sync hook unit
    useEffect(() => {
        if (formUnitValue && formUnitValue !== unit) {
            setUnit(formUnitValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formUnitValue]);

    // When unit changes in UI, persist to form (if nameUnit provided)
    useEffect(() => {
        if (!nameUnit) return;
        if (formik.values?.[nameUnit] !== unit) {
            formik.setFieldValue(nameUnit, unit);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unit]);

    const handleSelectChange = (_e: any, data: any) => {
        const newUnit = data.value as WeightUnit;
        setUnit(newUnit);
        // keep displayed value in sync by converting the canonical kg -> display in effect
    };

    const handleInputChange = (_e: any, data: any) => {
        setDisplayValue(data.value);
    };

    const handleInputBlur = async () => {
        const kg = parseToKg(displayValue);
        await formik.setFieldValue(nameKg, kg);
        await formik.setFieldTouched(nameKg, true, false);
        // refresh display (in case parsing rounded or unit changed)
        setDisplayValue(toDisplay(kg));
    };

    return (<>
        <Field name={nameUnit}>
            {({ field }: { field: FieldInputProps<any> }) => (
                <SFormSelect
                    label="Unit"
                    options={selectOptions}
                    value={unit}
                    onChange={handleSelectChange}
                    placeholder="Unit"
                    disabled={disabled}
                />
            )}
        </Field>

        <SFormInput
            label={label}>
            <Input
                value={displayValue}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                type="number"
                step="0.1"
                style={{ minWidth: 0 }}
                // show unit on right using Semantic UI Input's action area
                action={{ content: unit.toUpperCase(), disabled: true }}
                disabled={disabled}
            />
        </SFormInput>

    </>

    );
}